/**
 * API Client Service
 * Handles all HTTP communication with the COMP backend
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { msalInstance } from '../index';
import { silentRequest } from '../authConfig';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000
});

// Request interceptor - attach Entra ID bearer token
apiClient.interceptors.request.use(async (config) => {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const response = await msalInstance.acquireTokenSilent({
        ...silentRequest,
        account: accounts[0]
      });
      config.headers.Authorization = `Bearer ${response.accessToken}`;
    }
  } catch (error) {
    // Token refresh failed - user will need to re-authenticate
    console.error('Token acquisition failed', error);
  }
  return config;
});

// Response interceptor - handle 401 by clearing MSAL cache and redirecting
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        // No account in cache — redirect to login
        await msalInstance.loginRedirect(silentRequest as any).catch(() => {});
      }
      // If account exists but token was rejected, let the error propagate
      // so the UI can show the error without looping
    }
    return Promise.reject(error);
  }
);

// ----------------------------------------------------------------
// Contracts API
// ----------------------------------------------------------------
export const contractsApi = {
  list: (params?: Record<string, any>) => apiClient.get('/contracts', { params }),
  dashboard: () => apiClient.get('/contracts/dashboard'),
  get: (id: string) => apiClient.get(`/contracts/${id}`),
  create: (data: Record<string, any>) => apiClient.post('/contracts', data),
  update: (id: string, data: Record<string, any>) => apiClient.put(`/contracts/${id}`, data),
  delete: (id: string) => apiClient.delete(`/contracts/${id}`),
  claudeExtract: (id: string) => apiClient.post(`/contracts/${id}/extract`, {}, { timeout: 120000 }),
  history: (id: string) => apiClient.get(`/contracts/${id}/history`),
  getAmendments: (id: string) => apiClient.get(`/contracts/${id}/amendments`),
  createAmendment: (id: string, data: Record<string, any>) => apiClient.post(`/contracts/${id}/amendments`, data)
};

// ----------------------------------------------------------------
// Obligations API
// ----------------------------------------------------------------
export const obligationsApi = {
  list: (params?: Record<string, any>) => apiClient.get('/obligations', { params }),
  get: (id: string) => apiClient.get(`/obligations/${id}`),
  create: (data: Record<string, any>) => apiClient.post('/obligations', data),
  update: (id: string, data: Record<string, any>) => apiClient.put(`/obligations/${id}`, data),
  delete: (id: string) => apiClient.delete(`/obligations/${id}`),
  addMeasurement: (id: string, data: Record<string, any>) => apiClient.post(`/obligations/${id}/measurements`, data)
};

// ----------------------------------------------------------------
// Documents API
// ----------------------------------------------------------------
export const documentsApi = {
  list: (contractId: string) => apiClient.get(`/documents/${contractId}`),
  upload: (contractId: string, formData: FormData, onProgress?: (p: number) => void, force = false) =>
    apiClient.post(`/documents/${contractId}/upload${force ? '?force=1' : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress && onProgress(Math.round((e.loaded * 100) / (e.total || 1)))
    }),
  // `inline` asks the backend for Content-Disposition: inline so the browser
  // can render the file in a tab instead of saving it.
  download: (documentId: string, inline = false) =>
    apiClient.get(`/documents/file/${documentId}${inline ? '?inline=1' : ''}`, {
      responseType: 'blob',
      timeout: 120000
    }),
  delete: (documentId: string) => apiClient.delete(`/documents/file/${documentId}`)
};

// ----------------------------------------------------------------
// Notifications API
// ----------------------------------------------------------------
export const notificationsApi = {
  list: (params?: Record<string, any>) => apiClient.get('/notifications', { params }),
  markRead: (id: string) => apiClient.put(`/notifications/${id}/read`),
  markAllRead: () => apiClient.put('/notifications/read-all'),
  getRules: () => apiClient.get('/notifications/rules'),
  createRule: (data: Record<string, any>) => apiClient.post('/notifications/rules', data),
  updateRule: (id: string, data: Record<string, any>) => apiClient.put(`/notifications/rules/${id}`, data),
  deleteRule: (id: string) => apiClient.delete(`/notifications/rules/${id}`)
};

// ----------------------------------------------------------------
// Reports API
// ----------------------------------------------------------------
export const reportsApi = {
  contracts: (params?: Record<string, any>) => apiClient.get('/reports/contracts', { params }),
  obligations: (params?: Record<string, any>) => apiClient.get('/reports/obligations', { params }),
  renewals: (params?: Record<string, any>) => apiClient.get('/reports/renewals', { params }),
  audit: (params?: Record<string, any>) => apiClient.get('/reports/audit', { params }),
  exportContracts: () => apiClient.get('/reports/export/contracts', { responseType: 'blob' })
};

// ----------------------------------------------------------------
// Users API
// ----------------------------------------------------------------
export const usersApi = {
  me: () => apiClient.get('/users/me'),
  list: () => apiClient.get('/users'),
  create: (data: Record<string, any>) => apiClient.post('/users', data),
  get: (id: string) => apiClient.get(`/users/${id}`),
  updateRole: (id: string, role: string) => apiClient.put(`/users/${id}/role`, { role }),
  updateStatus: (id: string, is_active: boolean) => apiClient.put(`/users/${id}/status`, { is_active }),
  approve: (id: string, is_approved: boolean, send_invite = false) => apiClient.put(`/users/${id}/approve`, { is_approved, send_invite }),
  delete: (id: string) => apiClient.delete(`/users/${id}`),
  search: (q: string) => apiClient.get('/users/search/typeahead', { params: { q } }),
  updateMyPreferences: (prefs: { daily_summary_enabled?: boolean }) =>
    apiClient.patch('/users/me/preferences', prefs)
};

// ----------------------------------------------------------------
// Admin API
// ----------------------------------------------------------------
// ----------------------------------------------------------------
// Imports API
// ----------------------------------------------------------------
export const importsApi = {
  preview: (formData: FormData) =>
    apiClient.post('/imports/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    }),
  confirm: (formData: FormData, force = false) =>
    apiClient.post(`/imports/confirm${force ? '?force=1' : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    })
};

// ----------------------------------------------------------------
// Admin API
// ----------------------------------------------------------------
// ----------------------------------------------------------------
// Search API
// ----------------------------------------------------------------
export const searchApi = {
  global: (q: string, limit = 20) => apiClient.get('/search', { params: { q, limit } }),
  contract: (contractId: string, q: string) =>
    apiClient.get(`/search/contract/${contractId}`, { params: { q } })
};

// ----------------------------------------------------------------
// Admin API
// ----------------------------------------------------------------
export const adminApi = {
  getSettings: () => apiClient.get('/admin/settings'),
  getPublicSetting: (key: string) => apiClient.get(`/admin/settings/public/${key}`),
  updateSetting: (key: string, value: string) => apiClient.put(`/admin/settings/${key}`, { value }),
  triggerReminders: () => apiClient.post('/admin/reminders/trigger'),
  triggerDailySummary: () => apiClient.post('/admin/daily-summary/trigger'),
  testEmail: () => apiClient.post('/admin/email/test'),
  getStats: () => apiClient.get('/admin/stats')
};

export default apiClient;
